import { environment } from '#environments';
import { ResponseItem } from '#models';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

/**
 * Service for managing response items in functional reviews.
 *
 * Handles creation, updating, and deletion of response items including:
 * - Action items with priority levels
 * - Comments and notes
 *
 * @remarks
 * Response items are associated with functional review responses and can be
 * either action items (with priority) or simple comments.
 *
 * @example
 * ```typescript
 * constructor(private responseItemService: ResponseItemService) {}
 *
 * addActionItem() {
 *   this.responseItemService.addActionItem({
 *     action: 'Review documentation',
 *     priority: 'High',
 *     responseId: 123,
 *     comment: 'Needs immediate attention'
 *   }).subscribe();
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class ResponseItemService {
  /** HTTP client for making API requests */
  private _http = inject(HttpClient);

  /**
   * Creates a new action item for a functional review response.
   *
   * @param responseItem - The action item data to create
   * @param responseItem.action - The action description
   * @param responseItem.priority - Priority level of the action
   * @param responseItem.responseId - ID of the associated response
   * @param responseItem.comment - Optional comment or additional notes
   * @returns Observable that emits when the action item is created
   *
   * @remarks
   * Automatically sets isActionItem flag to true
   *
   * @example
   * ```typescript
   * this.addActionItem({
   *   action: 'Update configuration',
   *   priority: 'Medium',
   *   responseId: 456,
   *   comment: 'Follow up next week'
   * }).subscribe(() => {
   *   console.log('Action item created');
   * });
   * ```
   */
  addActionItem(responseItem: Pick<ResponseItem, 'action' | 'priority' | 'responseId' | 'comment'>) {
    return this._http.post(`${environment.apiUrl}/response-items`, { ...responseItem, isActionItem: true });
  }

  /**
   * Updates an existing action item.
   *
   * @param responseItemId - The ID of the action item to update
   * @param responseItem - The updated action item data
   * @param responseItem.action - The updated action description
   * @param responseItem.priority - Updated priority level
   * @param responseItem.responseId - ID of the associated response
   * @param responseItem.comment - Updated comment or notes
   * @returns Observable that emits when the action item is updated
   *
   * @example
   * ```typescript
   * this.updateActionItem(123, {
   *   action: 'Review and approve',
   *   priority: 'High',
   *   responseId: 456,
   *   comment: 'Urgent update'
   * }).subscribe();
   * ```
   */
  updateActionItem(
    responseItemId: number,
    responseItem: Pick<ResponseItem, 'action' | 'priority' | 'responseId' | 'comment'>
  ) {
    return this._http.put(`${environment.apiUrl}/response-items/${responseItemId}`, {
      ...responseItem,
      isActionItem: true,
    });
  }

  /**
   * Deletes a response item (action item or comment).
   *
   * @param id - The ID of the response item to delete
   * @returns Observable that emits when the item is deleted
   *
   * @example
   * ```typescript
   * this.deleteResponseItem(123).subscribe(() => {
   *   console.log('Response item deleted');
   * });
   * ```
   */
  deleteResponseItem(id: number) {
    return this._http.delete(`${environment.apiUrl}/response-items/${id}`);
  }

  /**
   * Adds a comment to a functional review response.
   *
   * @param responseItem - The comment data to create
   * @param responseItem.comment - The comment text
   * @param responseItem.responseId - ID of the associated response
   * @returns Observable that emits when the comment is created
   *
   * @remarks
   * Automatically sets isActionItem flag to false
   *
   * @example
   * ```typescript
   * this.addComment({
   *   comment: 'This looks good',
   *   responseId: 789
   * }).subscribe();
   * ```
   */
  addComment(responseItem: Pick<ResponseItem, 'comment' | 'responseId'>) {
    return this._http.post(`${environment.apiUrl}/response-items`, { ...responseItem, isActionItem: false });
  }

  /**
   * Updates an existing comment.
   *
   * @param responseItemId - The ID of the comment to update
   * @param responseItem - The updated comment data
   * @param responseItem.comment - The updated comment text
   * @param responseItem.responseId - ID of the associated response
   * @returns Observable that emits when the comment is updated
   *
   * @example
   * ```typescript
   * this.updateComment(123, {
   *   comment: 'Updated observation',
   *   responseId: 789
   * }).subscribe();
   * ```
   */
  updateComment(responseItemId: number, responseItem: Pick<ResponseItem, 'comment' | 'responseId'>) {
    return this._http.put(`${environment.apiUrl}/response-items/${responseItemId}`, {
      ...responseItem,
      isActionItem: false,
    });
  }
}
